import React, { useState, useEffect, useMemo } from 'react';
import { useModalDismiss } from '../../../hooks/useModalDismiss';
import axios from '../../../Api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSpinner, faTrash, faTrashAlt, faMagnifyingGlass, faCircleCheck, faRotateLeft, faFloppyDisk } from '@fortawesome/free-solid-svg-icons';
import ConfirmationModal from './ConfirmationModal';
import SearchableSelect from '../../../components/SearchableSelect';

/**
 * CT/CTS management of a quota project's location addresses: search/filter, per-row and
 * multi-select delete (visited locations can't be deleted), and a visited toggle.
 */
const PENDING_LABEL = { create: 'Ajout en attente', update: 'Modif. en attente', delete: 'Suppression en attente' };

const LocationManageModal = ({ quotaProjectId, onClose, onChange, onAssigned }) => {
  // Editable list: only Escape and the explicit buttons dismiss it.
  const dismiss = useModalDismiss(onClose, { backdropClose: false });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // { kind: 'single'|'bulk', row? }
  // Prestataires an address may be bound to, with their cap and remaining room.
  const [assignables, setAssignables] = useState([]);
  const [assigningId, setAssigningId] = useState(null);
  // Draft prestataire per row: { [rowId]: supervisorId|null }. A change is only sent when
  // the row's save button is pressed, so a mis-click never reassigns anything.
  const [draftAssign, setDraftAssign] = useState({});

  const fetchRows = async () => {
    try {
      setLoading(true);
      const [res, supRes] = await Promise.all([
        axios.get(`/quotas/projects/${quotaProjectId}/locations`),
        axios.get(`/quotas/projects/${quotaProjectId}/assignable-supervisors`).catch(() => ({ data: { data: [] } })),
      ]);
      setAssignables(supRes.data?.data || []);
      setRows(res.data.data || []);
    } catch (err) {
      setError('Erreur lors du chargement des adresses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotaProjectId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(r => {
      if (statusFilter === 'unassigned') {
        if (r.supervisor) return false;
      } else if (statusFilter !== 'all' && r.status !== statusFilter) {
        return false;
      }
      if (q) {
        const hay = `${r.name || ''} ${r.agency_code || ''} ${r.address_text || ''} ${r.supervisor?.name || ''} ${r.supervisor?.ref || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, search, statusFilter]);

  // Pending rows (create/update/delete not yet approved) can't be acted on again here.
  const selectableIds = useMemo(() => filtered.filter(r => r.status !== 'visited' && !r.pending_operation).map(r => r.id), [filtered]);
  const allSelected = selectableIds.length > 0 && selectableIds.every(id => selected.has(id));

  const toggleSelectAll = () => {
    setSelected(prev => {
      const next = new Set(prev);
      if (allSelected) selectableIds.forEach(id => next.delete(id));
      else selectableIds.forEach(id => next.add(id));
      return next;
    });
  };

  const toggleSelect = (row) => {
    if (row.status === 'visited' || row.pending_operation) return;
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(row.id)) next.delete(row.id); else next.add(row.id);
      return next;
    });
  };

  const handleToggleVisited = async (row) => {
    try {
      const res = await axios.post(`/quotas/locations/${row.id}/visited`);
      setRows(prev => prev.map(r => r.id === row.id ? { ...r, status: res.data.status } : r));
      if (res.data.status === 'visited') {
        setSelected(prev => { const n = new Set(prev); n.delete(row.id); return n; });
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors du changement de statut');
    }
  };

  /**
   * Bind / move / unassign an address's prestataire.
   *
   * The server re-checks everything (prestataire assigned to the project, the
   * per-prestataire cap, pair uniqueness, visited-freeze), so a rejection is surfaced
   * as-is and the row is left untouched. On success the assignable list is refetched so
   * the used/remaining counts stay honest.
   */
  const handleAssign = async (row) => {
    const supervisorId = draftAssign[row.id] === undefined ? (row.supervisor?.id || null) : draftAssign[row.id];
    if ((row.supervisor?.id || null) === supervisorId) return;
    setAssigningId(row.id);
    setError('');
    try {
      const res = await axios.patch(`/quotas/locations/${row.id}/supervisor`, { supervisor_id: supervisorId });
      const updated = res.data?.data;
      setRows(prev => prev.map(r => r.id === row.id
        ? { ...r, supervisor: updated?.supervisor ?? null, ref: updated?.ref ?? null }
        : r));
      const supRes = await axios
        .get(`/quotas/projects/${quotaProjectId}/assignable-supervisors`)
        .catch(() => null);
      if (supRes) setAssignables(supRes.data?.data || []);
      setDraftAssign(prev => { const n = { ...prev }; delete n[row.id]; return n; });
      // Deliberately NOT onChange(): that refetches the whole quota project, and
      // QuotaBuilder renders a full-page spinner while loading, which would unmount this
      // modal. The row and the prestataire counts are already updated locally; onAssigned
      // just refreshes the map markers behind.
      if (onAssigned) onAssigned();
    } catch (err) {
      setError(err.response?.data?.message || "Erreur lors de l'assignation");
    } finally {
      setAssigningId(null);
    }
  };

  const handleDeleteRow = (row) => {
    if (row.status === 'visited') return;
    setConfirm({ kind: 'single', row });
  };

  const handleBulkDelete = () => {
    if (selected.size === 0) return;
    setConfirm({ kind: 'bulk' });
  };

  // Executes the pending confirm (single row or bulk).
  const doConfirmedDelete = async () => {
    const c = confirm;
    setConfirm(null);
    if (!c) return;
    try {
      setBusy(true);
      if (c.kind === 'single') {
        const row = c.row;
        // Deletions apply immediately. `pending_operation` only ever appears on a leftover
        // row from the old approval mechanism, which is cancelled rather than deleted.
        const res = row.pending_operation
          ? await axios.delete(`/quotas/pending-locations/${row.pending_id}`)
          : await axios.delete(`/quotas/locations/${row.id}`);
        setSelected(prev => { const n = new Set(prev); n.delete(row.id); return n; });
        await fetchRows();
        if (res.data?.pending) setError('Suppression en attente de validation par le chef terrain supérieur.');
      } else {
        const ids = Array.from(selected);
        const res = await axios.post('/quotas/locations/bulk-delete', { ids });
        const skipped = res.data.skipped_visited || [];
        const skippedClosed = res.data.skipped_closed || [];
        setSelected(new Set());
        await fetchRows();
        if (skipped.length > 0 || skippedClosed.length > 0) {
          const reasons = [
            skipped.length ? `${skipped.length} ignorée(s) car déjà visitée(s).` : null,
            skippedClosed.length ? `${skippedClosed.length} ignorée(s) car le projet est terminé ou archivé.` : null,
          ].filter(Boolean).join(' ');
          setError(`${res.data.deleted} supprimée(s). ${reasons}`);
        }
      }
      if (onChange) onChange();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la suppression');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" {...dismiss}>
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Gérer les adresses</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <FontAwesomeIcon icon={faTimes} className="text-xl" />
          </button>
        </div>

        <div className="px-6 py-3 border-b border-gray-200 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[180px]">
            <FontAwesomeIcon icon={faMagnifyingGlass} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher (nom, code, prestataire...)"
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 outline-none" />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="py-2 px-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 outline-none">
            <option value="all">Tous les statuts</option>
            <option value="pending">À visiter</option>
            <option value="visited">Visité</option>
            <option value="unassigned">Non assignées</option>
          </select>
          {selected.size > 0 && (
            <button onClick={handleBulkDelete} disabled={busy}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50">
              <FontAwesomeIcon icon={faTrashAlt} /> Supprimer ({selected.size})
            </button>
          )}
        </div>

        {error && <div className="mx-6 mt-3 p-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">{error}</div>}

        <div className="flex-1 overflow-y-auto px-6 py-3">
          {loading ? (
            <div className="flex items-center justify-center py-10"><FontAwesomeIcon icon={faSpinner} className="animate-spin text-red-600 text-2xl" /></div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-gray-500 py-10">Aucune adresse.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 uppercase border-b border-gray-200">
                  <th className="py-2 w-8">
                    <input type="checkbox" checked={allSelected} onChange={toggleSelectAll}
                      className="w-4 h-4 text-red-600 border-gray-300 rounded" title="Tout sélectionner (non visités)" />
                  </th>
                  <th className="py-2">Emplacement</th>
                  <th className="py-2">Prestataire</th>
                  <th className="py-2">Statut</th>
                  <th className="py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map(r => {
                  const isVisited = r.status === 'visited';
                  const isPending = !!r.pending_operation;
                  const locked = isVisited || isPending;
                  return (
                    <tr key={r.id} className={`hover:bg-gray-50 ${isPending ? 'bg-yellow-50' : ''}`}>
                      <td className="py-2">
                        <input type="checkbox" checked={selected.has(r.id)} disabled={locked}
                          onChange={() => toggleSelect(r)}
                          className={`w-4 h-4 text-red-600 border-gray-300 rounded ${locked ? 'opacity-40 cursor-not-allowed' : ''}`}
                          title={isPending ? 'Modification en attente de validation' : (isVisited ? 'Adresse visitée — non supprimable' : 'Sélectionner')} />
                      </td>
                      <td className="py-2">
                        <div className="font-medium text-gray-900">{r.name}{r.agency_code ? ` (${r.agency_code})` : ''}</div>
                        {r.address_text && <div className="text-xs text-gray-500 truncate max-w-[220px]">{r.address_text}</div>}
                      </td>
                      <td className="py-2 text-gray-700">
                        {/* A visited address is frozen — reassigning it would move somebody
                            else's completed fieldwork. Pending rows aren't actionable here. */}
                        {r.status === 'visited' || isPending ? (
                          <div>
                            <div>{r.supervisor?.name || <span className="text-amber-700">Non assigné</span>}</div>
                            {r.supervisor?.ref && (
                              <div className="text-xs text-gray-500">({r.supervisor.ref})</div>
                            )}
                          </div>
                        ) : (() => {
                          const draft = draftAssign[r.id];
                          const currentId = r.supervisor?.id || null;
                          const value = draft === undefined ? currentId : draft;
                          const dirty = value !== currentId;
                          const chosen = assignables.find(sup => sup.id === value);
                          return (
                            <div>
                              <div className="flex items-center gap-1.5">
                                <div className={`w-[210px] rounded-md ${dirty ? 'ring-2 ring-red-400' : ''}`}>
                                  <SearchableSelect
                                    options={assignables}
                                    selectedValue={value}
                                    onChange={(id) => setDraftAssign(prev => ({ ...prev, [r.id]: id ?? null }))}
                                    disabled={assigningId === r.id || busy}
                                    placeholder="Non assigné"
                                    searchPlaceholder="Nom ou réf..."
                                    maxHeight="200px"
                                    triggerClassName="px-2 py-1"
                                    textClassName="text-xs"
                                    // Ref/count live on the line below the control;
                                    // repeating them in the trigger made the cell cramped.
                                    showTriggerMeta={false}
                                    getOptionLabel={(sup) => sup.name}
                                    // remaining already accounts for this row when it is theirs;
                                    // the component can't disable single options, so a full
                                    // prestataire is flagged here and refused by the server.
                                    getOptionMeta={(sup) => `${sup.ref || '—'} · ${sup.used}/${sup.assigned}`
                                      + (sup.id !== currentId && sup.remaining <= 0 ? ' · complet' : '')}
                                  />
                                </div>

                                {/* Nothing is sent until this is pressed. */}
                                {dirty && (
                                  <button
                                    type="button"
                                    onClick={() => handleAssign(r)}
                                    disabled={assigningId === r.id || busy}
                                    title="Enregistrer l'assignation"
                                    className="p-1.5 rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                                  >
                                    <FontAwesomeIcon icon={assigningId === r.id ? faSpinner : faFloppyDisk}
                                      className={assigningId === r.id ? 'animate-spin' : ''} />
                                  </button>
                                )}
                                {dirty && assigningId !== r.id && (
                                  <button
                                    type="button"
                                    onClick={() => setDraftAssign(prev => { const n = { ...prev }; delete n[r.id]; return n; })}
                                    title="Annuler la modification"
                                    className="p-1.5 rounded-md text-gray-400 hover:text-gray-600"
                                  >
                                    <FontAwesomeIcon icon={faRotateLeft} />
                                  </button>
                                )}
                              </div>

                              {/* Ref + address count of whoever is selected. */}
                              <div className="text-xs text-gray-500 mt-0.5">
                                {chosen
                                  ? <>({chosen.ref || '—'}) · {chosen.used}/{chosen.assigned} adresse(s)</>
                                  : <span className="text-amber-700">Aucun prestataire</span>}
                              </div>
                            </div>
                          );
                        })()}
                      </td>
                      <td className="py-2">
                        {isPending ? (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            {PENDING_LABEL[r.pending_operation] || 'En attente'}
                          </span>
                        ) : (
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${isVisited ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                            {isVisited ? 'Visité' : 'À visiter'}
                          </span>
                        )}
                      </td>
                      <td className="py-2">
                        <div className="flex items-center justify-end gap-1">
                          {!isPending && (
                            <button onClick={() => handleToggleVisited(r)}
                              className="p-1.5 rounded text-gray-600 hover:text-gray-800 hover:bg-gray-100"
                              title={isVisited ? 'Marquer non visité' : 'Marquer visité'}>
                              <FontAwesomeIcon icon={isVisited ? faRotateLeft : faCircleCheck} />
                            </button>
                          )}
                          <button onClick={() => handleDeleteRow(r)} disabled={isVisited || busy}
                            className={`p-1.5 rounded ${isVisited ? 'text-gray-300 cursor-not-allowed' : 'text-red-600 hover:text-red-700 hover:bg-red-50'}`}
                            title={isPending ? 'Annuler cette modification en attente' : (isVisited ? 'Adresse visitée — marquez-la non visitée pour la supprimer' : 'Supprimer')}>
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-6 py-3 border-t border-gray-200 flex justify-between items-center">
          <span className="text-sm text-gray-500">{filtered.length} adresse(s)</span>
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Fermer</button>
        </div>
      </div>

      <ConfirmationModal
        show={!!confirm}
        title={confirm?.kind === 'bulk' ? 'Supprimer les adresses' : (confirm?.row?.pending_operation ? 'Annuler la modification' : "Supprimer l'adresse")}
        message={confirm?.kind === 'bulk'
          ? `Supprimer ${selected.size} adresse(s) sélectionnée(s) ?`
          : (confirm?.row?.pending_operation
            ? 'Annuler cette modification en attente ? Elle ne sera pas envoyée pour validation.'
            : `Supprimer « ${confirm?.row?.name} » ?`)}
        confirmText={confirm?.row?.pending_operation ? 'Annuler la modif.' : 'Supprimer'}
        cancelText="Retour"
        variant="danger"
        onConfirm={doConfirmedDelete}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
};

export default LocationManageModal;
